import { Injectable, NgZone } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot,  UrlTree, Router } from '@angular/router';
import { Subscription, firstValueFrom, timeout } from 'rxjs';
import { InitialisationService } from '@services/initialisation.service';
import { AuthenticationService } from '@services/authentication/authentication.service';
import {
  ClientConstants,
  BroadcastService,
  LocalStorageService,
  LocalStorageKeys,
  VersionService,
  SessionTestService,
  LoggedInUserService
} from '@formbird/services';

let isInitialized = false;
let isSaving = false;
let currentUrl;

const SESSION_TEST_TIMEOUT_MS = 10000;

@Injectable({
  providedIn: 'root',
})
export class CanActivateGuard implements CanActivate {
  constructor(public initialisationService: InitialisationService,
              public broadcastService: BroadcastService,
              private router: Router, private ngZone: NgZone,
              private localStorageService: LocalStorageService,
              private authenticationService: AuthenticationService,
              private sessionTestService: SessionTestService,
              private loggedInUserService: LoggedInUserService
              ) {
    this.broadcastService.on(ClientConstants.DOCUMENT_SAVE_STATUS).subscribe((value: any) => {
      isSaving = value?.isSaving;
      currentUrl = value?.url;
    });
  }
  async canActivate(
      route: ActivatedRouteSnapshot, state: RouterStateSnapshot
  ): Promise < boolean | UrlTree > {
      if (this.authenticationService.isLoggedOutOffline()) {
        // never restore a session the user signed out of while offline
        return this.finishOfflineLogout();
      }

      if (!this.localStorageService.getItem(LocalStorageKeys.LOGGED_IN_USER_ID)) {
        // the server session may already exist without the client knowing about it, e.g. after a SAML/social login
        // redirect, so check the session and load the logged in user before falling back to the login screen
        try {
          const res = await this.testSessionWithTimeout();
          if (res !== "OK") {
            this.authenticationService.showLogin();
            return false;
          }
          await this.loggedInUserService.setLoggedUser();
        } catch {
          this.authenticationService.showLogin();
          return false;
        }
      }

      this.initBeforeUnloadEvent();
      this.checkServiceWorkerActivated();
      return this.initializeServices();
  }

  // a slow connection shouldn't hold a signed-out user on a blank screen
  private testSessionWithTimeout(): Promise < any > {
    return firstValueFrom(this.sessionTestService.testSessionObservable().pipe(timeout(SESSION_TEST_TIMEOUT_MS)));
  }

  // the user signed out while offline, so the server session was left open and only the login form sends the
  // deferred logout request. With SAML auto redirect the login form is never shown, so finish the logout here
  private async finishOfflineLogout(): Promise < boolean > {
    try {
      const res = await this.testSessionWithTimeout();
      if (res === "OK") {
        // ends the server session, clears the offline logout flag and redirects
        await this.authenticationService.sendOfflineLogoutRequest();
        return false;
      }
    } catch (err: any) {
      if (err?.status === 401) {
        // the server session has already ended, but an SSO session would sign the user straight back in through the
        // SAML auto redirect, so finish the logout through the SSO sign out URL when there is one
        this.localStorageService.removeItem(ClientConstants.OFFLINE_LOGOUT);
        this.authenticationService.logoutConfirmed();
        return false;
      }
    }

    this.authenticationService.showLogin();
    return false;
  }

  getBaseUrl() {
    return (<any>window).document.getElementsByTagName("base")[0].href.split('/')[3] || "";
  }

  checkServiceWorkerActivated() {
    const baseUrl = this.getBaseUrl();
    const appVersion = this.localStorageService.getItem(LocalStorageKeys.APP_VERSION);
    const key = baseUrl + LocalStorageKeys.SERVICE_WORKER_ACTIVATED;

    const serviceWorkerActivated = JSON.parse(localStorage.getItem(key) || '{}');
    const isActivated = serviceWorkerActivated[appVersion];

    console.log(`Old service worker activated item: `, serviceWorkerActivated);
    
    if (isActivated === false) {
      
      if (VersionService.needsUpdate(appVersion)) {
        localStorage.setItem(key, `{ "${appVersion}": true }`);
        console.log(`Setting item for key:  ${key}: { "${appVersion}": true }`);
        window.location.reload();
    
      } else {
        console.log(`Do not update item: ${key} for version ${appVersion} in case ${isActivated}`);
      }
    
    } else if (!VersionService.hasBreakingApiChanges() && isActivated) {
      localStorage.setItem(key, `{ "${appVersion}": false }`);
      console.log(`Setting item for key:  ${key}: { "${appVersion}": false }`);
    
    } else {
      console.log(`Do not update item: ${key} for version ${appVersion} in case ${isActivated}`);
    }

  }

  async initializeServices(): Promise < boolean > {
    try {
      const curNavigation = this.router.getCurrentNavigation();
      if (curNavigation?.trigger === "popstate" && curNavigation?.previousNavigation?.trigger === "popstate"){
        window.location.reload();
      } else if (isSaving) {
        const isCompleted = await this.waitToCompleteSaving();
        if (isCompleted && currentUrl) {
          this.ngZone.run(() => this.router.navigateByUrl(currentUrl));
        } else if (!isCompleted){
          window.location.reload();
        } else {
          return false;
        }
      } else if (isInitialized){
        return true;
      }else{
        this.initialisationService.setupAppInfo();
        this.initialisationService.setupServices();
        this.initialisationService.initialise();
        await this.initialisationService.postLoginInitialise();
        isInitialized = true;
        return true;
      }
    } catch (error) {
      if (error.status === 502 || error.status === 504) {
        return true;
      }
    }
  }

  private async waitToCompleteSaving(): Promise < boolean > {
    return new Promise(async (resolve, reject) => {
      const config = (<any>window).ftClientConfig;
      let timeoutCount = 0;
      const inner = async () => {
        if (!isSaving) {
          resolve(true);
        } else {
          if ( config?.saveCompleteTimeout && (timeoutCount * 500) > config?.saveCompleteTimeout){
            resolve(false);
          } else {
            timeoutCount++;
            return setTimeout(inner,500);
          }
        }
      };

      await inner();
    })
  }

  private initBeforeUnloadEvent() {
    async function unloadHandler(event) {
      if (isSaving){
        event.preventDefault();
        return event.returnValue = `Are you sure you want to leave?`;
      }
    }
    window.addEventListener('beforeunload', async function (e) {
      return await unloadHandler(e);
    });

    window.addEventListener("visibilitychange", async function(e)
    {
      if (document.visibilityState == 'hidden') {
        return await unloadHandler(e);
      }
    });
  }
}
